import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Table, Td, Textarea } from "../ui";

const EMPTY = {
    name: "", contact_person: "", phone: "", email: "", address: "", gst: "", license_no: "",
    payment_terms: "", is_manufacturer: true, is_active: true, notes: "",
};

export default function RhmSuppliers() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState(null);

    const { data: suppliers = [], isLoading } = useQuery({
        queryKey: ["rhm-suppliers"],
        queryFn: async () => (await rhm.get("/suppliers")).data,
    });

    const save = useMutation({
        mutationFn: async (payload) =>
            payload.id
                ? (await rhm.patch(`/suppliers/${payload.id}`, payload)).data
                : (await rhm.post("/suppliers", payload)).data,
        onSuccess: () => { toast.success("Supplier saved"); setForm(null); qc.invalidateQueries({ queryKey: ["rhm-suppliers"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-suppliers-page">
            <PageHead title="Suppliers & Manufacturers" sub="Sources of stock for purchase orders."
                action={can("suppliers.create") && (
                    <Btn onClick={() => setForm(EMPTY)} data-testid="rhm-add-supplier">
                        <Plus className="w-3.5 h-3.5 inline mr-1.5" /> ADD SUPPLIER
                    </Btn>
                )} />

            <Card title={`${suppliers.length} supplier(s)`}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-suppliers-table" head={["Name", "Contact", "Phone", "GST", "Terms", "Type", ""]}
                        empty={!suppliers.length && <Empty msg="No suppliers yet." />}>
                        {suppliers.map((s) => (
                            <tr key={s.id}>
                                <Td className="font-semibold">{s.name}</Td>
                                <Td className="text-sm text-zinc-400">{s.contact_person || "—"}</Td>
                                <Td className="text-sm">{s.phone || "—"}</Td>
                                <Td className="mono text-xs">{s.gst || "—"}</Td>
                                <Td className="text-sm text-zinc-400">{s.payment_terms || "—"}</Td>
                                <Td><Badge tone={s.is_manufacturer ? "gold" : "muted"}>{s.is_manufacturer ? "MANUFACTURER" : "SUPPLIER"}</Badge></Td>
                                <Td>
                                    {can("suppliers.edit") && (
                                        <button onClick={() => setForm(s)} className="mono text-[10px] tracking-[0.16em] text-gold"
                                            data-testid={`rhm-edit-supplier-${s.id}`}>EDIT</button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Edit Supplier" : "Add Supplier"} testId="rhm-supplier-modal">
                {form && (
                    <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Name" required><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></Field>
                            <Field label="Contact person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
                            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                            <Field label="GST number"><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></Field>
                            <Field label="License number"><Input value={form.license_no} onChange={(e) => setForm({ ...form, license_no: e.target.value })} /></Field>
                            <Field label="Payment terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></Field>
                            <Field label="Address" className="md:col-span-2"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                            <Field label="Notes" className="md:col-span-2"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!form.is_manufacturer} onChange={(e) => setForm({ ...form, is_manufacturer: e.target.checked })} />
                            This party manufactures our products
                        </label>
                        <div className="flex gap-3">
                            <Btn type="submit" disabled={save.isPending} data-testid="supplier-submit">SAVE SUPPLIER</Btn>
                            <Btn type="button" variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
